import { TOKEN_IDS, type TokenId, type Token, LOCK_TOKEN_IDS } from "@/types";
import { mainnet } from "wagmi/chains";
import { isAddressEqual, type Address } from "viem";

export const DEFAULT_MINT_TOKEN = TOKEN_IDS.USDC;
export const DEFAULT_LOCK_TOKEN = TOKEN_IDS.IUSD;
export const DEFAULT_DEPOSIT_TOKEN = TOKEN_IDS.USDC;

// Ensure all token options are defined in TOKEN_IDS
export const MINT_TOKEN_OPTIONS = [TOKEN_IDS.USDC];
export const DEPOSIT_TOKEN_OPTIONS = [TOKEN_IDS.USDC, TOKEN_IDS.IUSD, TOKEN_IDS.USDT, TOKEN_IDS.SUSDE, TOKEN_IDS.USDE];

// Token contract addresses by network
export const TOKEN_ADDRESSES: Record<number, Record<TokenId, Address>> = {
    // [arbitrum.id]: {
    //     [TOKEN_IDS.USDC]: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831" as Address,
    //     [TOKEN_IDS.SIUSD]: "0xBaEff8bb3Fd2bcC1543ef30Ab3E8482A98F25350" as Address,
    //     [TOKEN_IDS.IUSD]: "0x2605862BeC1Cb6B74c319a5a17E437EB70fF5F30" as Address,
    //     // [TOKEN_IDS.DAI]: "0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1" as Address,
    //     [TOKEN_IDS.USDT]: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9" as Address,
    //     [TOKEN_IDS.SUSDE]: "0x211Cc4DD073734dA055fbF44a2b4667d5E5fE5d2" as Address,
    //     [TOKEN_IDS.USDE]: "0x4c9EDD5852cd905f086C759E8383e09bff1E68B3" as Address,
    //     [TOKEN_IDS.USDO]: "0x8238884Ec9668Ef77B90C6dfF4D1a9F4F4823BFe" as Address,
    // },
    [mainnet.id]: {
        [TOKEN_IDS.USDC]: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48" as Address,
        [TOKEN_IDS.IUSD]: "0x48f9e38f3070AD8945DFEae3FA70987722E3D89c" as Address,
        [TOKEN_IDS.SIUSD]: "0xDBDC1Ef57537E34680B898E1FEBD3D68c7389bCB" as Address,
        [TOKEN_IDS.USDT]: "0xdAC17F958D2ee523a2206206994597C13D831ec7" as Address,
        [TOKEN_IDS.SUSDE]: "0x9D39A5DE30e57443BfF2A8307A4256c8797A3497" as Address,
        [TOKEN_IDS.USDE]: "0x4c9EDD5852cd905f086C759E8383e09bff1E68B3" as Address,
        [TOKEN_IDS.USDO]: "0x8238884Ec9668Ef77B90C6dfF4D1a9F4F4823BFe" as Address,
        [TOKEN_IDS.LIUSD]: "0x12b004719fb632f1E7c010c6F5D6009Fb4258442" as Address,
        // underlying farm assets
        [LOCK_TOKEN_IDS.PT_USDE_25SEP2025]: "0xBC6736d346a5eBC0dEbc997397912CD9b8FAe10a" as Address,
        [LOCK_TOKEN_IDS.PT_SUSDE_25SEP2025]: "0x9F56094C450763769BA0EA9Fe2876070c0fD5F77" as Address,
        [LOCK_TOKEN_IDS.PT_WSTUSR_25SEP2025]: "0x23E60d1488525bf4685f53b3aa8E676c30321066" as Address,
        [LOCK_TOKEN_IDS.PT_SLVLUSD_25SEP2025]: "0x2CA5f2C4300450D53214B00546795c1c07B89acB" as Address,
        [LOCK_TOKEN_IDS.PT_USR_4SEP2025]: "0x5a5b93F762739fa94F3EcC0b34Af2e56702E7f70" as Address,
        //
        [LOCK_TOKEN_IDS.PT_TUSDE_25SEP2025]: "0xd0a4b74a6b62aa2b6c02349463d9041606608f36" as Address,
        [LOCK_TOKEN_IDS.PT_PUSDE_16OCT2025]: "0xf3f491e5608f8b8a6fd9e9d66a4a4036d7fd282c" as Address,
        [LOCK_TOKEN_IDS.PT_SUSDE_27NOV2025]: "0xe6a934089bbee34f832060ce98848359883749b3" as Address,
        [LOCK_TOKEN_IDS.PT_USDE_27NOV2025]: "0x62c6e813b9589c3631ba0cdb013acdb8544038b7" as Address,
        [LOCK_TOKEN_IDS.PT_TUSDE_18DEC2025]: "0x1135b22d6e8fd0809392478eedcd8c107db6af9d" as Address,
        [LOCK_TOKEN_IDS.PT_SYRUPUSDC_30OCT2025]: "0x00026e3311937bad48d9ab894c42134306e1698d" as Address,
        [LOCK_TOKEN_IDS.PT_WSRUSD_30OCT2025]: "0x10a099baf814b5fda138c4e6b60ed42c0d975be8" as Address,
        //
    },
};

// Base token configuration without addresses
export const TOKEN_CONFIG: Record<TokenId, Omit<Token, "address"> & { pt?: { expiry?: number } }> = {
    [TOKEN_IDS.USDC]: {
        id: TOKEN_IDS.USDC,
        symbol: "USDC",
        name: "USDC",
        decimals: 6,
    },
    [TOKEN_IDS.IUSD]: {
        id: TOKEN_IDS.IUSD,
        symbol: "iUSD",
        name: "infiniFi USD",
        decimals: 18,
    },
    [TOKEN_IDS.SIUSD]: {
        id: TOKEN_IDS.SIUSD,
        symbol: "siUSD",
        name: "Staked iUSD",
        decimals: 18,
    },
    [TOKEN_IDS.LIUSD]: {
        id: TOKEN_IDS.LIUSD,
        symbol: "liUSD",
        name: "Locked iUSD",
        decimals: 18,
    },
    [TOKEN_IDS.USDT]: {
        id: TOKEN_IDS.USDT,
        symbol: "USDT",
        name: "Tether USD",
        decimals: 6,
    },
    [TOKEN_IDS.SUSDE]: {
        id: TOKEN_IDS.SUSDE,
        symbol: "sUSDe",
        name: "Staked USDe (Ethena)",
        decimals: 18,
    },
    [TOKEN_IDS.USDE]: {
        id: TOKEN_IDS.USDE,
        symbol: "USDe",
        name: "USDe (Ethena)",
        decimals: 18,
    },
    [TOKEN_IDS.USDO]: {
        id: TOKEN_IDS.USDO,
        symbol: "USDO",
        name: "USD Origin",
        decimals: 18,
    },
    [TOKEN_IDS.PT_USDE_25SEP2025]: {
        id: TOKEN_IDS.PT_USDE_25SEP2025,
        symbol: "PT-USDe-25SEP2025",
        name: "PT-USDe-25SEP2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-09-25") },
    },
    [TOKEN_IDS.PT_SUSDE_25SEP2025]: {
        id: TOKEN_IDS.PT_SUSDE_25SEP2025,
        symbol: "PT-sUSDe-25SEP2025",
        name: "PT-sUSDe-25SEP2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-09-25") },
    },
    [TOKEN_IDS.PT_WSTUSR_25SEP2025]: {
        id: TOKEN_IDS.PT_WSTUSR_25SEP2025,
        symbol: "PT-wstUSR-25SEP2025",
        name: "PT-wstUSR-25SEP2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-09-25") },
    },
    [TOKEN_IDS.PT_USR_4SEP2025]: {
        id: TOKEN_IDS.PT_USR_4SEP2025,
        symbol: "PT-USR-4SEP2025",
        name: "PT-USR-4SEP2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-09-04") },
    },
    [TOKEN_IDS.PT_SLVLUSD_25SEP2025]: {
        id: TOKEN_IDS.PT_SLVLUSD_25SEP2025,
        symbol: "PT-slvlUSD-25SEP2025",
        name: "PT-slvlUSD-25SEP2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-09-25") },
    },
    [TOKEN_IDS.PT_TUSDE_25SEP2025]: {
        id: TOKEN_IDS.PT_TUSDE_25SEP2025,
        symbol: "PT-tUSDe-25SEP2025",
        name: "PT-tUSDe-25SEP2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-09-25") },
    },
    [TOKEN_IDS.PT_PUSDE_16OCT2025]: {
        id: TOKEN_IDS.PT_PUSDE_16OCT2025,
        symbol: "PT-pUSDe-16OCT2025",
        name: "PT-pUSDe-16OCT2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-10-16") },
    },
    [TOKEN_IDS.PT_SUSDE_27NOV2025]: {
        id: TOKEN_IDS.PT_SUSDE_27NOV2025,
        symbol: "PT-sUSDe-27NOV2025",
        name: "PT-sUSDe-27NOV2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-11-27") },
    },
    [TOKEN_IDS.PT_USDE_27NOV2025]: {
        id: TOKEN_IDS.PT_USDE_27NOV2025,
        symbol: "PT-USDe-27NOV2025",
        name: "PT-USDe-27NOV2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-11-27") },
    },
    [TOKEN_IDS.PT_TUSDE_18DEC2025]: {
        id: TOKEN_IDS.PT_TUSDE_18DEC2025,
        symbol: "PT-tUSDe-18DEC2025",
        name: "PT-tUSDe-18DEC2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-12-18") },
    },
    [TOKEN_IDS.PT_SYRUPUSDC_30OCT2025]: {
        id: TOKEN_IDS.PT_SYRUPUSDC_30OCT2025,
        symbol: "PT-SyrupUSDC-30OCT2025",
        name: "PT-SyrupUSDC-30OCT2025",
        decimals: 6,
        pt: { expiry: Date.parse("2025-10-30") },
    },
    [TOKEN_IDS.PT_WSRUSD_30OCT2025]: {
        id: TOKEN_IDS.PT_WSRUSD_30OCT2025,
        symbol: "PT-wsrUSD-30OCT2025",
        name: "PT-wsrUSD-30OCT2025",
        decimals: 18,
        pt: { expiry: Date.parse("2025-10-30") },
    },
};

// Helper function to get a complete token configuration with address
export const getTokenWithAddress = (chainId: number, tokenId: TokenId): Token => {
    const baseToken = TOKEN_CONFIG[tokenId];
    const address = getTokenAddress(chainId, tokenId);

    return {
        ...baseToken,
        address,
    };
};

export const getTokenAddress = (chainId: number, tokenId: TokenId): Address => {
    // First check if we have a direct address in TOKEN_ADDRESSES
    if (TOKEN_ADDRESSES[chainId]?.[tokenId]) {
        return TOKEN_ADDRESSES[chainId][tokenId] as Address;
    }

    throw new Error(`Token address not found for ${tokenId} on chain ID ${chainId}`);
};

export const getTokenIdByAddress = (chainId: number, address: Address): TokenId => {
    const tokenEntries = Object.entries(TOKEN_ADDRESSES[chainId] || {});
    for (const [tokenId, tokenAddress] of tokenEntries) {
        if (isAddressEqual(tokenAddress, address)) {
            return tokenId as TokenId;
        }
    }
    throw new Error(`Token ID not found for address ${address} on chain ID ${chainId}`);
};

export const isExpiredToken = (tokenId: TokenId): boolean => {
    const token = TOKEN_CONFIG[tokenId];
    if (token.pt?.expiry) {
        return token.pt.expiry < Date.now();
    }
    return false;
};
